import { toConvexTableId, toStorageId } from "~/lib/convex-table-id";

import type { OnlineOrderingFormValues, SettingsInput } from "./types";
import { DEFAULT_VALUES } from "./types";

// ─── Helpers ────────────────────────────────────────

export function centsToDisplay(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function displayToCents(display: string): number {
  return Math.round(Number.parseFloat(display || "0") * 100);
}

function timestampToDatetimeLocal(ts?: number): string {
  if (!ts) {
    return "";
  }
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function datetimeLocalToTimestamp(datetime: string): number | undefined {
  if (!datetime) {
    return undefined;
  }
  return new Date(datetime).getTime();
}

function withDefaults<T>(value: T | undefined | null, fallback: T): T {
  return value ?? fallback;
}

function applySettingsDefaults(
  s: SettingsInput
): Omit<
  OnlineOrderingFormValues,
  | "slug"
  | "isLive"
  | "pickupEnabled"
  | "deliveryEnabled"
  | "curbsideEnabled"
  | "pauseUntil"
> {
  const d = DEFAULT_VALUES;
  return {
    defaultPrepTimeMinutes: withDefaults(
      s.defaultPrepTimeMinutes,
      d.defaultPrepTimeMinutes
    ),
    deliveryTimeMinutes: withDefaults(
      s.deliveryTimeMinutes,
      d.deliveryTimeMinutes
    ),
    scheduleSlotIntervalMinutes: withDefaults(
      s.scheduleSlotIntervalMinutes,
      d.scheduleSlotIntervalMinutes
    ),
    futureOrdersEnabled: withDefaults(
      s.futureOrdersEnabled,
      d.futureOrdersEnabled
    ),
    futureOrderMaxDays: withDefaults(
      s.futureOrderMaxDays,
      d.futureOrderMaxDays
    ),
    autoAcceptOrders: withDefaults(s.autoAcceptOrders, d.autoAcceptOrders),
    autoAcceptDelaySeconds: withDefaults(
      s.autoAcceptDelaySeconds,
      d.autoAcceptDelaySeconds
    ),
    tipOptions: withDefaults(s.tipOptions, d.tipOptions),
    defaultTipPercent: withDefaults(s.defaultTipPercent, d.defaultTipPercent),
    minimumOrderAmount: withDefaults(
      s.minimumOrderAmount,
      d.minimumOrderAmount
    ),
    deliveryMinimumAmount: withDefaults(
      s.deliveryMinimumAmount,
      d.deliveryMinimumAmount
    ),
    deliveryFee: withDefaults(s.deliveryFee, d.deliveryFee),
    logoStorageId: withDefaults(s.logoStorageId, d.logoStorageId),
    bannerImageUrl: withDefaults(s.bannerImageUrl, d.bannerImageUrl),
    accentColor: withDefaults(s.accentColor, d.accentColor),
    bannerStorageId: withDefaults(s.bannerStorageId, d.bannerStorageId),
    faviconStorageId: withDefaults(s.faviconStorageId, d.faviconStorageId),
    ogImageStorageId: withDefaults(s.ogImageStorageId, d.ogImageStorageId),
    secondaryColor: withDefaults(s.secondaryColor, d.secondaryColor),
    fontFamily: withDefaults(s.fontFamily, d.fontFamily),
    welcomeMessage: withDefaults(s.welcomeMessage, d.welcomeMessage),
    maxPickupOrdersPerSlot: withDefaults(
      s.maxPickupOrdersPerSlot,
      d.maxPickupOrdersPerSlot
    ),
    maxDeliveryOrdersPerSlot: withDefaults(
      s.maxDeliveryOrdersPerSlot,
      d.maxDeliveryOrdersPerSlot
    ),
    maxCurbsideOrdersPerSlot: withDefaults(
      s.maxCurbsideOrdersPerSlot,
      d.maxCurbsideOrdersPerSlot
    ),
    isPaused: withDefaults(s.isPaused, d.isPaused),
    pauseMessage: withDefaults(s.pauseMessage, d.pauseMessage),
    useCustomOnlineHours: (s.onlineOrderingHours?.length ?? 0) === 7,
    onlineOrderingHours:
      s.onlineOrderingHours?.length === 7
        ? s.onlineOrderingHours.map((h) => ({
            dayOfWeek: h.dayOfWeek,
            isOpen: h.isOpen,
            openTime: h.openTime ?? "09:00",
            closeTime: h.closeTime ?? "21:00",
          }))
        : d.onlineOrderingHours,
    scheduleOverrides: (s.scheduleOverrides ?? []).map((o) => ({
      date: o.date,
      type: o.type,
      openTime: o.openTime ?? "09:00",
      closeTime: o.closeTime ?? "21:00",
      reason: o.reason ?? "",
    })),
  };
}

export function buildSettingsPayload(
  v: OnlineOrderingFormValues,
  locationId: string
) {
  return {
    locationId: toConvexTableId("locations", locationId),
    slug: v.slug,
    isLive: v.isLive,
    pickupEnabled: v.pickupEnabled,
    deliveryEnabled: v.deliveryEnabled,
    curbsideEnabled: v.curbsideEnabled,
    defaultPrepTimeMinutes: v.defaultPrepTimeMinutes,
    deliveryTimeMinutes: v.deliveryTimeMinutes,
    scheduleSlotIntervalMinutes: v.scheduleSlotIntervalMinutes,
    futureOrdersEnabled: v.futureOrdersEnabled,
    futureOrderMaxDays: v.futureOrderMaxDays,
    autoAcceptOrders: v.autoAcceptOrders,
    autoAcceptDelaySeconds: v.autoAcceptDelaySeconds,
    tipOptions: v.tipOptions,
    defaultTipPercent: v.defaultTipPercent,
    minimumOrderAmount: v.minimumOrderAmount,
    deliveryMinimumAmount: v.deliveryMinimumAmount,
    deliveryFee: v.deliveryFee,
    logoStorageId: v.logoStorageId ? toStorageId(v.logoStorageId) : undefined,
    bannerImageUrl: v.bannerImageUrl ?? undefined,
    accentColor: v.accentColor || undefined,
    bannerStorageId: v.bannerStorageId
      ? toStorageId(v.bannerStorageId)
      : undefined,
    faviconStorageId: v.faviconStorageId
      ? toStorageId(v.faviconStorageId)
      : undefined,
    ogImageStorageId: v.ogImageStorageId
      ? toStorageId(v.ogImageStorageId)
      : undefined,
    secondaryColor: v.secondaryColor || undefined,
    fontFamily: v.fontFamily || undefined,
    welcomeMessage: v.welcomeMessage || undefined,
    maxPickupOrdersPerSlot: v.maxPickupOrdersPerSlot || undefined,
    maxDeliveryOrdersPerSlot: v.maxDeliveryOrdersPerSlot || undefined,
    maxCurbsideOrdersPerSlot: v.maxCurbsideOrdersPerSlot || undefined,
    isPaused: v.isPaused,
    pauseMessage: v.pauseMessage || undefined,
    pauseUntil: datetimeLocalToTimestamp(v.pauseUntil),
    onlineOrderingHours: v.useCustomOnlineHours
      ? v.onlineOrderingHours.map((h) => ({
          dayOfWeek: h.dayOfWeek,
          isOpen: h.isOpen,
          openTime: h.openTime || undefined,
          closeTime: h.closeTime || undefined,
        }))
      : undefined,
    scheduleOverrides:
      v.scheduleOverrides.length > 0
        ? v.scheduleOverrides.map((o) => ({
            date: o.date,
            type: o.type,
            openTime:
              o.type === "modified" ? o.openTime || undefined : undefined,
            closeTime:
              o.type === "modified" ? o.closeTime || undefined : undefined,
            reason: o.reason || undefined,
          }))
        : undefined,
  };
}

export function settingsToFormValues(
  s: SettingsInput
): OnlineOrderingFormValues {
  return {
    ...applySettingsDefaults(s),
    slug: s.slug,
    isLive: s.isLive,
    pickupEnabled: s.pickupEnabled,
    deliveryEnabled: s.deliveryEnabled,
    curbsideEnabled: s.curbsideEnabled,
    pauseUntil: timestampToDatetimeLocal(s.pauseUntil),
  };
}
